interface ITokenClaims {
  /** The user this token authenticates. */
  id?: string
  /** The client a portfolio user has stepped into, when they are working as one. */
  clientId?: string
  /** Issued-at, seconds since the epoch. Stamped by the server on every token it mints. */
  iat?: number
  /** Expiry, seconds since the epoch. */
  exp?: number
}

/**
 * The claims of a JWT, read without verifying it. Null if it does not parse as one.
 *
 * Fine for what the client does with them — deciding whether a token is worth sending — and
 * nothing more: the server verifies every token it is handed.
 */
export const decodeTokenClaims = (token: string): ITokenClaims | null => {
  try {
    const base64UrlDecode = (str: string): string => {
      str = str.replace(/-/g, '+').replace(/_/g, '/')
      const pad = str.length % 4
      if (pad) str += '='.repeat(4 - pad)
      return atob(str)
    }

    return JSON.parse(base64UrlDecode(token.split('.')[1])) as ITokenClaims
  } catch {
    return null
  }
}

// Helper function to check if token is expired
export const isTokenExpired = (token: string): boolean => {
  const payload = decodeTokenClaims(token)
  // Undecodable is treated as expired: it is not a token we can use either way.
  if (!payload?.exp) return true

  const currentTime = Date.now() / 1000
  // 30 seconds buffer to account for network latency and server processing
  const buffer = 30

  return payload.exp < currentTime + buffer
}

/**
 * Whether a token the server handed back in a response header should replace the one in hand.
 *
 * Sessions slide: an ageing token is answered with a fresh one, and the client swaps it in.
 * That swap used to take the header on trust, and the header is not always fresh. The browser
 * caches API answers, and a 304 hands the page the stored copy — headers included — so a GET
 * that once carried a re-issued token kept carrying that same token from the cache. Once it was
 * seven days old, every login was followed by the client adopting a dead token off a 304 and
 * being signed out half a second later. The server side of this is fixed (API answers are no
 * longer cacheable), but the client should never have accepted a token older than its own.
 *
 * So a replacement has to be strictly newer than the current token by issue time, and not
 * expired. Same token, older token, expired token, or anything that does not decode: keep what
 * we have. With no current token there is nothing to compare against, and the header is the
 * only thing that could sign this tab in — refuse it.
 */
export const shouldAdoptRotatedToken = (candidate: string, current: string | null): boolean => {
  if (!current || candidate === current) return false
  if (isTokenExpired(candidate)) return false

  const candidateIssuedAt = decodeTokenClaims(candidate)?.iat
  const currentIssuedAt = decodeTokenClaims(current)?.iat
  if (!candidateIssuedAt || !currentIssuedAt) return false

  return candidateIssuedAt > currentIssuedAt
}

export const removeToken = () => {
  window.localStorage.removeItem('token')
  window.localStorage.removeItem('return_url')
}

/**
 * What a tab should do when another tab changes the shared token.
 *
 * `adopt` carries on in the new session without the user noticing; `reload` re-derives the
 * whole page because what is on screen no longer matches who the token says we are; `signOut`
 * tears down locally; `ignore` leaves this tab exactly as it is.
 */
export type CrossTabTokenChange =
  | { action: 'ignore' }
  | { action: 'adopt'; token: string }
  | { action: 'reload' }
  | { action: 'signOut' }

/**
 * Decide how a tab reacts to the token changing underneath it in shared storage.
 *
 * `ApiClient` reads `localStorage` once, in its constructor, and then works from an in-memory
 * copy. That copy is deliberate: `localStorage` is shared by every tab, so a request needs a
 * stable per-tab notion of "the token I am using" for a 401 to be attributable to it at all —
 * without that, a reply landing after another tab wrote to storage is judged against a token
 * that was never sent, which is how a stale tab used to sign a healthy one out.
 *
 * But reading it exactly once freezes the tab at construction. Sign out and back in on one tab,
 * click anything on another, and the second tab reports the session as invalid and drops to the
 * login screen — while a perfectly good token sits in storage, which is why a manual refresh
 * "fixes" it. The isolation we need is only that another tab's token cannot be blamed for our
 * failure, or evicted by us. Never learning that a newer one exists was never part of it.
 *
 * So the constructor stops being the only sync point. The rules, in order:
 *
 * - A tab with no token of its own is left alone. It is on the login screen, and pulling it
 *   into a session it did not ask for would be a stranger surprise than the bug.
 * - The token disappearing means another tab signed out. That revokes the session server-side,
 *   so ours is dead too — tear down rather than wait to discover it on the next request.
 * - Anything not strictly newer and still valid is ignored, reusing the same guard the rotation
 *   header uses. A tab must never be walked backwards onto an older or expired token.
 * - A newer token for the same user, in the same client, is adopted silently. This is the
 *   ordinary case, and the ordinary case should be invisible.
 * - A newer token for a *different* user, or the same user who has stepped into or out of a
 *   client, means what is rendered no longer matches who we are. Swapping the token alone would
 *   leave one person's screens filled in with another's data, so the page is re-derived instead.
 */
export const classifyCrossTabTokenChange = (
  incoming: string | null,
  current: string | null,
): CrossTabTokenChange => {
  if (!current) return { action: 'ignore' }
  if (!incoming) return { action: 'signOut' }
  if (incoming === current) return { action: 'ignore' }
  if (!shouldAdoptRotatedToken(incoming, current)) return { action: 'ignore' }

  const incomingClaims = decodeTokenClaims(incoming)
  const currentClaims = decodeTokenClaims(current)

  const sameUser = !!incomingClaims?.id && incomingClaims.id === currentClaims?.id
  const sameClient = incomingClaims?.clientId === currentClaims?.clientId

  return sameUser && sameClient ? { action: 'adopt', token: incoming } : { action: 'reload' }
}
